import type { Transport } from './transport.ts';
import type { EventSubscription } from './events.ts';
import type {
  AppLink, TrustedApp, TrustedAppInput, EmailAlias, AdminAddress, HostedDomain, MailLogEntry, MailLogFilter, MfaStatus, JunkScanPage, QrSignInStart, QrSignInStatus, PendingInvite, InviteResult, User, ApiToken, ApiTokenScope, AskResult, Migration, MigrationRecord, DomainTemplate, DomainTemplateEntry, TemplateDesign, AskTurn, BatchAction, Brief, ComposePayload, Contact, ContactInput, DraftRequest,
  ExtractedContact, FolderCount, ImportResult, ListMessagesParams, MeResponse, MessageDetail, MessagePage,
  MessageSummary, SendResult, SenderListEntry, SharedPreferences, SummaryResponse, TriageItem,
} from './types.ts';

const enc = encodeURIComponent;

/** Typed wrapper over every DTS Mail API endpoint. Platform-neutral: give it any {@link Transport}. */
/** POST files/uploads: PUT the file to uploadUrl with uploadHeaders, then put downloadUrl in the email. */
export interface SharedUploadLinks {
  fileName: string;
  uploadUrl: string;
  uploadExpiresUtc: string;
  uploadHeaders: Record<string, string>;
  downloadUrl: string;
  expiresUtc: string;
}

export class DtsMailClient {
  readonly transport: Transport;
  constructor(transport: Transport) {
    this.transport = transport;
  }

  private get<T>(path: string, query?: Record<string, any>) {
    return this.transport.request<T>({ method: 'GET', path, query });
  }
  private post<T>(path: string, body?: unknown, query?: Record<string, any>) {
    return this.transport.request<T>({ method: 'POST', path, body: body ?? {}, query });
  }

  // ---------- account ----------
  me = () => this.get<MeResponse>('me');
  /** Whether the server takes direct uploads of large attachments (404 on servers from before the feature). */
  uploadLimits = () => this.get<{ enabled: boolean; maxBytes: number; linkDays: number }>('files/uploads');
  /** A write link for uploading one file to the user's own storage, and the read-only download link (1 year) for the email. */
  createUpload = (fileName: string, contentType: string, size: number) =>
    this.post<SharedUploadLinks>('files/uploads', { fileName, contentType, size });
  changePassword = (currentPassword: string, newPassword: string) =>
    this.transport.request<void>({ method: 'POST', path: 'me/password', body: { currentPassword, newPassword }, responseType: 'none' });
  preferences = () => this.get<SharedPreferences>('me/preferences');
  savePreferences = (prefs: SharedPreferences) => this.transport.request<SharedPreferences>({ method: 'PUT', path: 'me/preferences', body: prefs });

  // ---------- messages ----------
  listMessages = (p: ListMessagesParams = {}) => this.get<MessagePage>('messages', { ...p });
  getMessage = (id: string, markRead = false) => this.get<MessageDetail>(`messages/${enc(id)}`, { markRead });
  thread = (id: string) => this.get<{ messages: MessageSummary[] }>(`messages/${enc(id)}/thread`).then((r) => r.messages);
  rawMessage = (id: string) => this.transport.request<string>({ method: 'GET', path: `messages/${enc(id)}/raw`, responseType: 'text' });
  attachmentBase64 = (id: string, index: number) =>
    this.transport.request<string>({ method: 'GET', path: `messages/${enc(id)}/attachments/${index}`, responseType: 'base64' });
  updateMessage = (id: string, patch: { isRead?: boolean; isFlagged?: boolean; folder?: string }) =>
    this.transport.request<MessageSummary>({ method: 'PATCH', path: `messages/${enc(id)}`, body: patch });
  batch = (ids: string[], action: BatchAction, folder?: string) =>
    // blocked: block-list entries added when mail is filed as junk (sender domain, or the address for shared providers).
    // unsubscribed: newsletters left with one-click unsubscribe (RFC 8058) because their mail was filed as junk.
    this.post<{ results: { id: string; ok: boolean; error?: string }[]; blocked?: string[]; unsubscribed?: string[]; alsoJunked?: string[] }>('messages/batch', { ids, action, folder });
  deleteMessage = (id: string, permanent = false) =>
    this.transport.request<void>({ method: 'DELETE', path: `messages/${enc(id)}`, query: { permanent }, responseType: 'none' });
  markJunk = (id: string, addSenderToList = false) =>
    this.post<{ id: string; blocked?: string | null; unsubscribed?: string[]; alsoJunked?: string[] }>(`messages/${enc(id)}/junk`, { addSenderToList });
  markNotJunk = (id: string, addSenderToList = false) => this.post(`messages/${enc(id)}/not-junk`, { addSenderToList });
  send = (payload: ComposePayload) => this.post<SendResult>('messages/send', payload);
  saveDraft = (payload: ComposePayload, id?: string) =>
    id
      ? this.transport.request<{ id: string }>({ method: 'PUT', path: `drafts/${enc(id)}`, body: payload })
      : this.post<{ id: string }>('drafts', payload);
  sendDraft = (id: string) => this.post<SendResult>(`drafts/${enc(id)}/send`);

  // ---------- folders ----------
  folders = () => this.get<{ folders: FolderCount[] }>('folders').then((r) => r.folders);
  /** A domain-named folder ("apple.com") is a filing rule: existing mail from that domain moves in (moved). */
  createFolder = (name: string) => this.post<{ name: string; moved: number; domainRule: boolean }>('folders', { name });
  /** Creates (or reuses) the folder named after the sender's domain and moves all mail from that domain into it, Junk included. */
  /** Stores a message from another mail app (raw RFC 822, base64). Keeps its date; duplicates (same Message-ID) are skipped. */
  importMessage = (rawBase64: string, folder: string, isRead: boolean) =>
    this.post<{ duplicate: boolean; id: string; folder?: string }>('messages/import', { raw: rawBase64, folder, isRead });
  fileDomain = (address: string) => this.post<{ name: string; created: boolean; moved: number }>('folders/domain', { address });
  /** Removes a custom folder after moving all its mail into moveTo; merging one folder into another is the same call. */
  /** Renames or moves a folder by changing its path ("Apple" → "Shopping/Apple"); subfolders and mail go with it. */
  renameFolder = (from: string, to: string) =>
    this.post<{ name: string; moved: number; filed: number; domainRule: boolean }>('folders/rename', { from, to });
  /** Subfolders go too. A subfolder's path ("Shopping/apple.com") contains '/', so it is passed in the query. */
  deleteFolder = (name: string, moveTo = 'Inbox') =>
    this.transport.request<{ deleted: string; moved: number; movedTo: string }>(
      name.includes('/')
        ? { method: 'DELETE', path: 'folders', query: { name, moveTo } }
        : { method: 'DELETE', path: `folders/${enc(name)}`, query: { moveTo } },
    );
  emptyFolder = (name: 'Trash' | 'Junk') => this.post<{ deleted: number }>(`folders/${name}/empty`);
  /** Runs the junk filter again over one page of the Inbox (contacts and safe senders skip the AI check); junk moves to Junk. */
  junkScan = (continuation?: string, skipErrors = false) => this.post<JunkScanPage>('messages/junk-scan', { continuation, skipErrors });

  // ---------- safe / block lists ----------
  senderList = (list: 'safe' | 'block') => this.get<{ entries: SenderListEntry[] }>(`lists/${list}`).then((r) => r.entries);
  addToSenderList = (list: 'safe' | 'block', entries: string[]) => this.post(`lists/${list}`, { entries });
  removeFromSenderList = (list: 'safe' | 'block', entry: string) =>
    this.transport.request({ method: 'DELETE', path: `lists/${list}/${enc(entry)}`, responseType: 'none' });

  // ---------- API tokens ----------
  apiTokens = () => this.get<{ tokens: ApiToken[] }>('tokens').then((r) => r.tokens);
  /** `from` is required for scope `send` (one of your addresses) and refused otherwise. */
  createApiToken = (name: string, scope: ApiTokenScope, expiresInDays?: number, from?: string) =>
    this.post<{ token: ApiToken; secret: string }>('tokens', { name, scope, expiresInDays, from });
  revokeApiToken = (id: string) => this.transport.request({ method: 'DELETE', path: `tokens/${enc(id)}`, responseType: 'none' });

  // ---------- contacts ----------
  contacts = (search?: string) => this.get<{ contacts: Contact[]; total: number }>('contacts', { search });
  contactsByEmail = (email: string) => this.get<{ contacts: Contact[] }>('contacts', { email }).then((r) => r.contacts);
  createContact = (c: ContactInput) => this.post<Contact>('contacts', c);
  updateContact = (c: Contact) => this.transport.request<Contact>({ method: 'PUT', path: `contacts/${enc(c.id)}`, body: c });
  deleteContact = (id: string) => this.transport.request({ method: 'DELETE', path: `contacts/${enc(id)}`, responseType: 'none' });
  deleteContacts = (ids: string[]) => this.post<{ deleted: number }>('contacts/delete', { ids });
  importContacts = (contacts: Partial<ContactInput>[], mode: 'merge' | 'skip' | 'create' = 'merge') =>
    this.post<ImportResult>('contacts/import', { contacts, mode });

  // ---------- push events (per-device Storage Queue) ----------
  subscribeEvents = (deviceId: string, platform: string) => this.post<EventSubscription>('me/events/subscribe', { deviceId, platform });
  unsubscribeEvents = (deviceId: string) => this.transport.request({ method: 'DELETE', path: `me/events/${enc(deviceId)}`, responseType: 'none' });
  testEvent = () => this.post<{ sent: boolean }>('me/events/test');

  // ---------- people & invitations (admin) ----------
  adminUsers = () => this.get<{ users: User[] }>('manage/users').then((r) => r.users);
  adminAddresses = (userId?: string) => this.get<{ addresses: AdminAddress[] }>('manage/addresses', { userId }).then((r) => r.addresses);
  adminDomains = () => this.get<{ domains: HostedDomain[] }>('manage/domains').then((r) => r.domains);
  updateUser = (id: string, patch: { displayName?: string; role?: string; disabled?: boolean }) =>
    this.transport.request<{ user: User }>({ method: 'PATCH', path: `manage/users/${enc(id)}`, body: patch });
  deleteUser = (id: string) => this.transport.request({ method: 'DELETE', path: `manage/users/${enc(id)}`, responseType: 'none' });
  assignAddress = (address: string, userId: string, displayName?: string) =>
    this.transport.request({ method: 'PUT', path: `manage/addresses/${enc(address)}`, body: { userId, displayName } });
  removeAddress = (address: string) => this.transport.request({ method: 'DELETE', path: `manage/addresses/${enc(address)}`, responseType: 'none' });
  setCatchAll = (domain: string, userId: string) => this.transport.request({ method: 'PUT', path: `manage/domains/${enc(domain)}`, body: { catchAllUserId: userId } });
  /** Replaces the domain's administrators (they can edit its brand template). */
  setDomainAdmins = (domain: string, userIds: string[]) =>
    this.transport.request<{ domain: HostedDomain }>({ method: 'PUT', path: `manage/domains/${enc(domain)}`, body: { adminUserIds: userIds } });
  invites = () => this.get<{ invites: PendingInvite[] }>('manage/invites').then((r) => r.invites);
  mfaStatus = () => this.get<MfaStatus>('me/mfa');
  /** New recovery codes (the old ones stop working); needs a current authenticator code. */
  regenerateRecoveryCodes = (code: string) => this.post<{ recoveryCodes: string[] }>('me/mfa/recovery-codes', { code }).then((r) => r.recoveryCodes);
  /** Phone sign-in by QR code: this (signed-in) device shows the code, the phone scans it, this device approves. */
  qrSignInStart = () => this.post<QrSignInStart>('auth/qr/start');
  qrSignInStatus = (code: string) => this.post<QrSignInStatus>('auth/qr/status', { code });
  /** Approves or declines the phone that scanned the code; declining an unscanned code cancels it. */
  qrSignInDecide = (code: string, approve: boolean) =>
    this.transport.request<void>({ method: 'POST', path: 'auth/qr/decide', body: { code, approve }, responseType: 'none' });
  /** Admin: clears someone's authenticator (lost phone) and signs them out; they set it up again at next sign-in. */
  resetUserMfa = (userId: string) => this.transport.request({ method: 'POST', path: `manage/users/${enc(userId)}/mfa/reset`, responseType: 'none' });
  mailLog = (p: { days?: number; filter?: MailLogFilter; search?: string; top?: number } = {}) =>
    this.get<{ items: MailLogEntry[]; total: number; days: number }>('manage/mail-log', { ...p });
  setMailLogVerdict = (mailbox: string, id: string, decision: 'junk' | 'not_junk', updateSenderList = false) =>
    this.post<{ folder: string; senderList?: string }>(`manage/mail-log/${enc(mailbox)}/${enc(id)}/verdict`, { decision, updateSenderList });
  createInvite = (req: { email: string; displayName?: string; addresses?: string[]; sendTo?: string; role?: 'user' | 'admin'; note?: string }) =>
    this.post<InviteResult>('manage/invites', req);
  resendInvite = (userId: string, sendTo?: string) => this.post<InviteResult>(`manage/invites/${enc(userId)}/resend`, { sendTo });
  revokeInvite = (userId: string, deleteUser = true) =>
    this.transport.request({ method: 'DELETE', path: `manage/invites/${enc(userId)}`, query: { deleteUser }, responseType: 'none' });

  // ---------- aliases (admin) ----------
  aliases = () => this.get<{ aliases: EmailAlias[]; warning?: string }>('manage/aliases');
  saveAlias = (address: string, body: { forwardTo: string[]; keepCopyUserId?: string | null; enabled?: boolean; note?: string }) =>
    this.transport.request<{ alias: unknown; verificationSentTo: string[]; warning?: string }>({ method: 'PUT', path: `manage/aliases/${enc(address)}`, body });
  deleteAlias = (address: string) => this.transport.request({ method: 'DELETE', path: `manage/aliases/${enc(address)}`, responseType: 'none' });
  resendAliasVerification = (email: string) => this.post<{ email: string; status: string }>('manage/aliases/verify', { email });

  // ---------- trusted apps & links (admin) ----------
  trustedApps = () => this.get<{ apps: TrustedApp[] }>('manage/apps').then((r) => r.apps);
  saveTrustedApp = (appId: string, app: TrustedAppInput) =>
    this.transport.request<TrustedApp>({ method: 'PUT', path: `manage/apps/${enc(appId)}`, body: app });
  deleteTrustedApp = (appId: string) => this.transport.request({ method: 'DELETE', path: `manage/apps/${enc(appId)}`, responseType: 'none' });
  appLinks = (filter: { appId?: string; userId?: string } = {}) =>
    this.get<{ links: AppLink[] }>('manage/links', filter).then((r) => r.links);
  /** subject is "<siteDomain>|<email>" (see appLinkSubject). */
  saveAppLink = (appId: string, subject: string, userId: string) =>
    this.transport.request<AppLink>({ method: 'PUT', path: `manage/links/${enc(appId)}/${enc(subject)}`, body: { userId } });
  deleteAppLink = (appId: string, subject: string) =>
    this.transport.request({ method: 'DELETE', path: `manage/links/${enc(appId)}/${enc(subject)}`, responseType: 'none' });

  // ---------- domain migration (admin) ----------
  migrations = () => this.get<{ migrations: Migration[] }>('migrations').then((r) => r.migrations);
  migration = (domain: string) => this.get<Migration>(`migrations/${enc(domain)}`);
  deleteMigration = (domain: string) => this.transport.request({ method: 'DELETE', path: `migrations/${enc(domain)}`, responseType: 'none' });
  inspectDomain = (domain: string) => this.post<Migration>('migrations/inspect', { domain });
  importMigrationRecords = (domain: string, input: { text?: string; imageBase64?: string; imageType?: string; fileName?: string }) =>
    this.post<Migration>(`migrations/${enc(domain)}/records`, input);
  updateMigrationRecords = (domain: string, records: MigrationRecord[]) =>
    this.transport.request<Migration>({ method: 'PUT', path: `migrations/${enc(domain)}/records`, body: { records } });
  copyMigration = (domain: string) => this.post<Migration>(`migrations/${enc(domain)}/copy`);
  verifyMigration = (domain: string) => this.post<Migration>(`migrations/${enc(domain)}/verify`);
  checkDelegation = (domain: string) => this.post<Migration>(`migrations/${enc(domain)}/delegation`);
  switchMigrationEmail = (domain: string) => this.post<Migration>(`migrations/${enc(domain)}/email`);
  rollbackMigrationEmail = (domain: string) => this.post<Migration>(`migrations/${enc(domain)}/email/rollback`);
  migrationHealth = (domain: string) => this.post<Migration>(`migrations/${enc(domain)}/health`);

  // ---------- domain branding templates ----------
  templates = () => this.get<{ domains: DomainTemplateEntry[] }>('templates').then((r) => r.domains);
  saveTemplate = (t: DomainTemplate) => this.transport.request<DomainTemplate>({ method: 'PUT', path: `templates/${enc(t.domain)}`, body: t });
  deleteTemplate = (domain: string) => this.transport.request({ method: 'DELETE', path: `templates/${enc(domain)}`, responseType: 'none' });
  designTemplate = (req: { domain: string; prompt: string; logoUrl?: string; currentHtml?: string; companyName?: string }) =>
    this.post<TemplateDesign>('ai/template', req);

  // ---------- AI ----------
  aiStatus = () => this.get<{ configured: boolean; model: string }>('ai/status');

  /** Streams a draft; onDelta receives each text chunk. Resolves with the full text. */
  async aiDraft(req: DraftRequest, onDelta: (chunk: string, full: string) => void, signal?: AbortSignal): Promise<string> {
    let full = '';
    let failure: string | null = null;
    await this.transport.stream({ method: 'POST', path: 'ai/draft', body: req }, (event, data) => {
      if (event === 'delta' && data?.text) {
        full += data.text;
        onDelta(data.text, full);
      } else if (event === 'error') failure = data?.error ?? 'AI draft failed.';
    }, signal);
    if (failure) throw new Error(failure);
    return full;
  }

  summarize = (messageId: string, refresh = false) => this.post<SummaryResponse>('ai/summarize', { messageId, refresh });
  triage = (ids: string[], force = false) => this.post<{ items: TriageItem[] }>('ai/triage', { ids, force }).then((r) => r.items);
  brief = () => this.post<Brief>('ai/brief');
  ask = (question: string, history: AskTurn[] = []) => this.post<AskResult>('ai/ask', { question, history });
  extractContact = (messageId: string) => this.post<ExtractedContact>('ai/extract-contact', { messageId });
  /** One Anthropic Messages turn through the server (used by the CRM agent loop). */
  agentTurn = (body: { system?: string; messages: unknown[]; tools?: unknown[]; max_tokens?: number; tool_choice?: unknown }) =>
    this.post<AnthropicResponse>('ai/agent', body);
}

export interface AnthropicContentBlock {
  type: 'text' | 'tool_use' | string;
  text?: string;
  id?: string;
  name?: string;
  input?: Record<string, unknown>;
}

export interface AnthropicResponse {
  id: string;
  role: 'assistant';
  content: AnthropicContentBlock[];
  stop_reason: 'end_turn' | 'tool_use' | 'max_tokens' | string;
}
